// "By Scene" view of the producer script book. Renders cues grouped under
// scene headers (with SMPTE in/out spans). Selection + edit state are owned
// by the parent route via the supplied callbacks/state.

import type { FrameRate } from '@ohear/shared'
import { copy, secondsToSmpteDisplay } from '@ohear/shared'
import { Card } from '@ohear/ui'
import type { CueRow, SceneSummary } from '../../lib/api/producer-types'
import { useT } from '../../lib/i18n/use-t'
import { CueRowDisplay } from './CueRowDisplay'

export interface ScriptBookByScenePropsRow {
  selected: boolean
  isEditing: boolean
}

export interface ScriptBookBySceneProps {
  scenes: SceneSummary[]
  cues: CueRow[]
  frameRate: FrameRate
  rowState: Map<string, ScriptBookByScenePropsRow>
  characterColors: Map<string, string>
  onToggleSelect: (cueId: string, next: boolean) => void
  onStartEdit: (cueId: string) => void
  onCancelEdit: () => void
  onSave: (cueId: string, patch: Partial<CueRow>) => void
  onSplit: (cueId: string) => void
  onMerge: (cueId: string) => void
}

export function ScriptBookByScene({
  scenes,
  cues,
  frameRate,
  rowState,
  characterColors,
  onToggleSelect,
  onStartEdit,
  onCancelEdit,
  onSave,
  onSplit,
  onMerge,
}: ScriptBookBySceneProps) {
  const t = useT()
  const cuesByScene = groupBySceneId(cues)

  if (scenes.length === 0) {
    return (
      <Card>
        <p className="m-0 text-[14px] text-[var(--text-2)]">
          No scenes detected. Re-run preprocessing or upload a fresh script.
        </p>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      {scenes.map((scene) => {
        const sceneCues = cuesByScene.get(scene.id) ?? []
        return (
          <Card
            key={scene.id}
            title={
              <div className="flex items-center gap-3">
                <span className="text-[14px] font-medium">Scene {scene.scene_number}</span>
                {scene.heading ? (
                  <span className="text-[12px] uppercase tracking-wide text-[var(--text-2)]">
                    {scene.heading}
                  </span>
                ) : null}
                <span className="text-[11px] font-mono text-[var(--text-3)]">
                  {secondsToSmpteDisplay(scene.in_sec, frameRate)} →{' '}
                  {secondsToSmpteDisplay(scene.out_sec, frameRate)}
                </span>
              </div>
            }
            actions={
              <span className="text-[12px] text-[var(--text-2)]">
                {sceneCues.length} cue{sceneCues.length === 1 ? '' : 's'}
              </span>
            }
          >
            {sceneCues.length === 0 ? (
              <p className="m-0 text-[13px] text-[var(--text-2)]">
                {t(copy.common.EMPTY.no_cues_in_scene)}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-[13px]">
                  <thead>
                    <tr className="text-left text-[11px] uppercase text-[var(--text-3)]">
                      <th className="w-6 px-2 py-1" />
                      <th className="w-12 px-2 py-1">#</th>
                      <th className="px-2 py-1">Char</th>
                      <th className="px-2 py-1">In / Out</th>
                      <th className="px-2 py-1">Line / Effort</th>
                      <th className="px-2 py-1">Predict</th>
                      <th className="px-2 py-1">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sceneCues.map((cue) => {
                      const state = rowState.get(cue.id)
                      const characterColor = cue.character_id
                        ? characterColors.get(cue.character_id)
                        : undefined
                      return (
                        <CueRowDisplay
                          key={cue.id}
                          cue={cue}
                          frameRate={frameRate}
                          selected={state?.selected ?? false}
                          isEditing={state?.isEditing ?? false}
                          characterColor={characterColor}
                          onSelect={(n) => onToggleSelect(cue.id, n)}
                          onStartEdit={() => onStartEdit(cue.id)}
                          onCancelEdit={onCancelEdit}
                          onSave={(patch) => onSave(cue.id, patch)}
                          onSplit={() => onSplit(cue.id)}
                          onMerge={() => onMerge(cue.id)}
                        />
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        )
      })}
    </div>
  )
}

function groupBySceneId(cues: CueRow[]): Map<string, CueRow[]> {
  const map = new Map<string, CueRow[]>()
  for (const cue of cues) {
    const arr = map.get(cue.scene_id)
    if (arr) arr.push(cue)
    else map.set(cue.scene_id, [cue])
  }
  return map
}
